// v2.19.27 — Cartes de rendu pour les documents non-ordonnance détectés par
// le magic flow : CNI, Facture, Analyse médicale. Style cohérent avec
// OrdonnanceCard mais plus compacts (~70L chacun).

const COLORS = {
  amber: "#F59E0B",
  amberSoft: "rgba(245,158,11,0.12)",
  amberBorder: "rgba(245,158,11,0.4)",
  blue: "#60A5FA",
  blueSoft: "rgba(96,165,250,0.10)",
  blueBorder: "rgba(96,165,250,0.35)",
  emerald: "#10B981",
  red: "#EF4444",
  redSoft: "rgba(239,68,68,0.12)",
  redBorder: "rgba(239,68,68,0.4)",
  slate: "#94A3B8",
};

const Box = ({ children, accent, "data-testid": tid, style }) => (
  <div data-testid={tid} style={{
    marginTop: 10, padding: "12px 14px", borderRadius: 12,
    background: `${accent}15`, border: `1px solid ${accent}66`,
    display: "flex", flexDirection: "column", gap: 8, ...(style || {}),
  }}>{children}</div>
);

const Row = ({ label, value }) => (
  <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 8, fontSize: 12 }}>
    <span style={{ color: "#94A3B8" }}>{label}</span>
    <span style={{ color: "#F1F5F9", fontWeight: 500 }}>{value || "—"}</span>
  </div>
);

const Alerts = ({ items, accent, icon = "⚠️" }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{
      padding: "8px 10px", background: `${accent}1A`,
      border: `1px solid ${accent}66`, borderRadius: 8,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: accent, marginBottom: 4 }}>
        {icon} {items.length === 1 ? "Alerte" : "Alertes"}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, color: "#FDE68A", fontSize: 11 }}>
        {items.map((a, i) => <li key={i}>{a}</li>)}
      </ul>
    </div>
  );
};

// ─── CountryNote (préfixe quand pays étranger détecté) ─────────────────────
export function CountryNote({ text }) {
  if (!text) return null;
  return (
    <div data-testid="country-note" style={{
      marginTop: 8, padding: "8px 12px",
      background: "rgba(96,165,250,0.10)", border: "1px solid rgba(96,165,250,0.35)",
      borderRadius: 8, color: "#93C5FD", fontSize: 12, fontStyle: "italic",
    }}>{text}</div>
  );
}

// ─── CniCard ────────────────────────────────────────────────────────────────
export function CniCard({ data }) {
  if (!data) return null;
  const TYPE_LABEL = { CNI: "🪪 Carte Nationale d'Identité", passeport: "🛂 Passeport", permis: "🚗 Permis de conduire" };
  const title = TYPE_LABEL[data.type_document] || "🪪 Pièce d'identité";
  return (
    <Box accent={COLORS.blue} data-testid="cni-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: COLORS.blue }}>{title}</h3>
        {data.pays_emetteur && <span style={{ fontSize: 11, color: "#CBD5E1" }}>📍 {data.pays_emetteur}</span>}
      </div>
      <Row label="Nom" value={data.nom} />
      <Row label="Prénoms" value={data.prenoms} />
      <Row label="Né(e) le" value={data.date_naissance} />
      <Row label="Lieu" value={data.lieu_naissance} />
      <Row label="N° document" value={data.numero} />
      <Row label="Émission" value={data.date_emission} />
      <Row label="Expiration" value={data.date_expiration} />
      <Alerts items={data.alertes_visuelles} accent={COLORS.amber} />
      <div style={{ fontSize: 10, color: COLORS.slate, fontStyle: "italic", marginTop: 4 }}>
        ⚠️ AYA n'authentifie pas les documents. Cette extraction est purement informative.
      </div>
    </Box>
  );
}

// ─── FactureCard ────────────────────────────────────────────────────────────
export function FactureCard({ data }) {
  if (!data) return null;
  return (
    <Box accent={COLORS.emerald} data-testid="facture-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: COLORS.emerald }}>🧾 Facture / Reçu</h3>
        {data.date && <span style={{ fontSize: 11, color: "#CBD5E1" }}>📅 {data.date}</span>}
      </div>
      <Row label="Vendeur" value={data.vendeur} />
      <Row label="IFU / IDU" value={data.ifu_idu} />
      {data.tva_pct && <Row label="TVA" value={`${data.tva_pct} %`} />}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
        <span style={{ fontSize: 11, color: "#94A3B8" }}>Total</span>
        <span style={{ fontSize: 22, fontWeight: 800, color: COLORS.emerald }}>
          {data.montant_total || "—"} {data.devise || ""}
        </span>
      </div>
      {Array.isArray(data.items) && data.items.length > 0 && (
        <div data-testid="facture-items" style={{
          border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, overflow: "hidden", fontSize: 11,
        }}>
          {data.items.map((it, i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", padding: "5px 9px",
              background: i % 2 === 0 ? "rgba(255,255,255,0.03)" : "transparent", color: "#CBD5E1",
            }}>
              <span>{it.libelle || "—"}</span>
              <span style={{ fontWeight: 600 }}>{it.prix || "—"}</span>
            </div>
          ))}
        </div>
      )}
      {Array.isArray(data.moyens_paiement) && data.moyens_paiement.length > 0 && (
        <div style={{ fontSize: 11, color: "#94A3B8" }}>
          💳 {data.moyens_paiement.join(", ")}
        </div>
      )}
      <Alerts items={data.alertes_suspicion} accent={COLORS.red} icon="🚨" />
    </Box>
  );
}

// ─── AnalyseMedicaleCard ────────────────────────────────────────────────────
const STATUT_COLORS = { normal: "#10B981", bas: "#F59E0B", haut: "#F59E0B", critique: "#EF4444" };
const STATUT_ICONS = { normal: "✅", bas: "⬇️", haut: "⬆️", critique: "🚨" };

export function AnalyseMedicaleCard({ data }) {
  if (!data) return null;
  return (
    <Box accent={COLORS.red} data-testid="analyse-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: COLORS.red }}>
          🧪 {data.type_examen || "Analyse médicale"}
        </h3>
        {data.date && <span style={{ fontSize: 11, color: "#CBD5E1" }}>📅 {data.date}</span>}
      </div>
      {Array.isArray(data.valeurs) && data.valeurs.length > 0 && (
        <div data-testid="analyse-values" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {data.valeurs.map((v, i) => (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "1fr auto auto",
              gap: 8, padding: "6px 10px",
              background: `${STATUT_COLORS[v.statut] || "#94A3B8"}12`,
              border: `1px solid ${STATUT_COLORS[v.statut] || "#94A3B8"}44`,
              borderRadius: 8, fontSize: 12, alignItems: "center",
            }}>
              <span style={{ color: "#F1F5F9", fontWeight: 600 }}>
                {STATUT_ICONS[v.statut] || ""} {v.parametre}
              </span>
              <span style={{ color: STATUT_COLORS[v.statut] || "#CBD5E1", fontWeight: 700 }}>
                {v.valeur} {v.unite}
              </span>
              <span style={{ color: "#94A3B8", fontSize: 10 }}>{v.reference}</span>
            </div>
          ))}
        </div>
      )}
      {data.interpretation_vulgarisee && (
        <div style={{
          padding: "8px 10px", background: "rgba(96,165,250,0.10)",
          border: "1px solid rgba(96,165,250,0.35)", borderRadius: 8,
          fontSize: 12, color: "#CBD5E1", lineHeight: 1.5,
        }}>
          💡 {data.interpretation_vulgarisee}
        </div>
      )}
      <Alerts items={data.alertes} accent={COLORS.red} />
      {Array.isArray(data.questions_a_poser_medecin) && data.questions_a_poser_medecin.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#A78BFA", marginBottom: 4 }}>
            ❓ Questions à poser au médecin
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: "#CBD5E1", fontSize: 11 }}>
            {data.questions_a_poser_medecin.map((q, i) => <li key={i}>{q}</li>)}
          </ul>
        </div>
      )}
      <div style={{ fontSize: 10, color: COLORS.slate, fontStyle: "italic", marginTop: 4 }}>
        ⚠️ Interprétation indicative. Consulte ton médecin pour toute décision.
      </div>
    </Box>
  );
}
